import { useTranslation } from "react-i18next";
import {
  buildUnassignedBreakdown,
  isDeletedPermanentCopy,
  type UnassignedBreakdownPayload,
} from "@/features/routes/lib/unassignedBreakdown";

interface Props {
  /** The `extra_data` breakdown block, already parsed. */
  payload: UnassignedBreakdownPayload;
  /**
   * Compact mode drops the per-reason suggestion text. Used inside the 320px
   * notification panel, where the names and reasons are the payload and the
   * advice would push them out of view.
   */
  compact?: boolean;
}

/**
 * Renders the unassigned breakdown: a translated reason heading per group with
 * the affected orders named underneath, plus an "and N more" line whenever the
 * backend capped the itemized list.
 *
 * Presentational only — no atom, no fetch — so the notification panel and any
 * other surface can drop it in. Grouping/translation live in
 * `unassignedBreakdown.ts`, which the routes-list alert shares, so all surfaces
 * word a reason identically.
 */
export function UnassignedReasonBreakdown({ payload, compact = false }: Props) {
  const { t } = useTranslation();
  const breakdown = buildUnassignedBreakdown(payload, t);

  if (breakdown.groups.length === 0) return null;

  return (
    <div className={compact ? "space-y-2" : "space-y-4"}>
      {breakdown.groups.map((group) => (
        <div key={group.reason}>
          <p
            className={`font-semibold text-gray-800 ${
              compact ? "text-[11px]" : "mb-1 text-xs"
            }`}
          >
            {group.label}
            <span className="ml-1 font-normal text-gray-500">
              ({group.total})
            </span>
          </p>

          {group.orderLabels.length > 0 ? (
            <ul className={compact ? "space-y-0.5" : "mb-1.5 space-y-0.5"}>
              {group.orders.map((order, index) => (
                <li
                  key={order.order_id}
                  className={`text-gray-700 ${compact ? "text-[11px]" : "text-xs"}`}
                >
                  {group.orderLabels[index]}
                  {/* BUG-677: the measured cause for THIS order, when sent. */}
                  {group.orderDetails[index] ? (
                    <span className="text-gray-500">
                      {" — "}
                      {group.orderDetails[index]}
                    </span>
                  ) : null}
                  {/* BUG-685: this date's copy of a permanent order is gone. */}
                  {isDeletedPermanentCopy(order) ? (
                    <span className="block text-gray-500">
                      {t("routes.buildAlert.permanentCopyRemoved")}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}

          {group.hiddenCount > 0 ? (
            <p
              className={`text-gray-500 ${compact ? "text-[11px]" : "mb-1.5 text-xs"}`}
            >
              {t("routes.buildAlert.showMore", { count: group.hiddenCount })}
            </p>
          ) : null}

          {!compact && group.suggestion ? (
            <p className="text-xs italic text-gray-600">{group.suggestion}</p>
          ) : null}
        </div>
      ))}
    </div>
  );
}
